import { useState, useEffect } from "react";
import { User, Phone, MapPin, Edit2, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react"; // Import Auth0 hook
import EditProfileModal from "./EditProfileModal";
import SignUp from "./SignUp";
import UserDetails from "./UserDetails";
import UserOrders from "./UserOrders";

function AuthLayout({ children }) {
  return (
    <div className="App flex flex-col min-h-screen items-center justify-center bg-gray-100">
      {children}
    </div>
  );
}

export default function UserProfileSection() {
  const [userDetails, setUserDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  

  const navigate = useNavigate();
  
  // Use Auth0 hooks instead of manual token handling
  const { 
    user: auth0User, 
    isLoading: auth0Loading, 
    isAuthenticated, 
    logout, 
    getAccessTokenSilently 
  } = useAuth0();
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const AUTH_API = process.env.REACT_APP_AUTH0_API;

  useEffect(() => {
    // Only fetch profile when Auth0 authentication is completed
    if (!auth0Loading && isAuthenticated && auth0User) {
      fetchUserProfile(auth0User.email);
    } else if (!auth0Loading && !isAuthenticated) {
      setLoading(false);
    }
  }, [auth0Loading, isAuthenticated, auth0User]);

  const fetchUserProfile = async (email) => {
  setLoading(true);
  setError("");
  
  try {
    // Get token from Auth0
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: AUTH_API // Make sure this matches your backend API_IDENTIFIER
      }
    });
    
    const encodedEmail = encodeURIComponent(email);
    //console.log("Fetching user profile for email:", encodedEmail);
    //console.log("Token (first few chars):", token.substring(0, 15) + "...");
    
    const response = await fetch(`${API_BASE_URL}/get-user-profile/${encodedEmail}/`, {
      method: 'GET',
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
        "Accept": "application/json"
      }
    });
    
    //console.log("Response status:", response.status);
    
    if (response.ok) {
      const data = await response.json();
      
      setUserDetails({
        fullName: data.name || auth0User.name || email,
        phoneNumber: data.phone || "Not set",
        address: data.address || {
          street: "Not set",
          city: "Not set",
          state: "Not set",
          zip: "Not set",
          country: "Not set"
        },
        email: data.email || email
      });
      
      setIsNewUser(false);
    } else if (response.status === 403) {
      // Handle forbidden access
      const errorData = await response.json();
      setError(errorData.error || "You don't have permission to access this profile");
      setIsNewUser(true);
    } else if (response.status === 404) {
      setIsNewUser(true);
    } else {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to fetch user profile");
    }
  } catch (err) {
    console.error("Error fetching profile:", err);
    setError("Failed to load profile. Please try again later.");
    setIsNewUser(true);
  } finally {
    setLoading(false);
  }
};
//console.log("User details:", userDetails);

  const handleEditProfile = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleSignOut = () => {
    // Use Auth0's logout function
    logout({ 
      returnTo: window.location.origin 
    });
  };

  if (auth0Loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#333333]">Checking authentication...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <AuthLayout>
        <SignUp redirectPath="/profile" />
      </AuthLayout>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#333333]">Loading Profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded shadow-lg p-6 max-w-md w-full">
          <p className="text-red-500 text-center mb-4">{error}</p>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 bg-white border border-red-500 text-red-500 px-4 py-2 rounded-sm text-sm font-medium hover:bg-red-50 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  if (isNewUser) {
    return <UserDetails email={auth0User.email} />;
  }

  return (
    <>
      <section className="min-h-screen py-12 px-4 md:px-8 max-w-7xl mx-auto bg-gradient-to-b from-gray-50 to-white mt-10">
        <div className="max-w-2xl mx-auto bg-white rounded-sm shadow-lg p-8">
          <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
            <h2 className="text-xl md:text-2xl text-[#333333]">
              Profile Information
            </h2>
            <button
              onClick={handleEditProfile}
              className="flex items-center gap-2 bg-[#D4AF37] text-black px-4 py-2 rounded-sm text-sm font-medium hover:bg-[#C19B2E] transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
            >
              <Edit2 className="h-4 w-4" />
              Edit Profile
            </button>
          </div>

          {userDetails && (
            <div className="space-y-6">
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-[#D4AF37] mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Full Name</p>
                  <p className="text-base text-[#333333]">{userDetails.fullName}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <User className="h-5 w-5 text-[#D4AF37] mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="text-base text-[#333333]">{userDetails.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#D4AF37] mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="text-base text-[#333333]">
                    {userDetails.address?.city || ""}, {userDetails.address?.country || ""}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-[#D4AF37] mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Phone Number</p>
                  <p className="text-base text-[#333333]">{userDetails.phoneNumber}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#D4AF37] mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Shipping Address</p>
                  <p className="text-base text-[#333333]">
                    {userDetails.address?.street || ""}, {userDetails.address?.city || ""}, {userDetails.address?.state || ""}, {userDetails.address?.zip || ""}, {userDetails.address?.country || ""}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handleSignOut}
              className="w-full flex items-center justify-center gap-2 bg-white border border-red-500 text-red-500 px-4 py-2 rounded-sm text-sm font-medium hover:bg-red-50 transition-all duration-200 shadow-sm hover:shadow-md"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </section>
      <UserOrders/>
      {isModalOpen && userDetails && (
        <EditProfileModal
          userDetails={userDetails}
          onClose={handleCloseModal}
          onSave={(updatedDetails) => {
            setUserDetails(updatedDetails);
            setIsModalOpen(false);
          }}
        />
      )}
    </>
  );
}
